// src/shared/hooks/useDeliveryDraft.js
import { useCallback, useEffect, useRef } from 'react';

const DRAFT_KEY = 'delivery_draft';
const DRAFT_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// Default times as unix millis
const defaultPickupMs  = () => Date.now() + 30 * 60000;
const defaultTargetMs  = () => Date.now() + 3 * 60 * 60000;
const defaultLatestPickUpMs    = () => Date.now() + 90 * 60000;
const defaultEarliestTargetMs  = () => Date.now() + 150 * 60000;

/**
 * Normalize a draft object — ensures all fields exist with sane defaults.
 * Times are stored as unix millis (number).
 */
function normalizeDraft(data) {
  return {
    // Location
    origin: data?.origin || "",
    destination: data?.destination || "",
    originData: data?.originData || null,
    destData: data?.destData || null,
    distance: data?.distance || null,
    duration: data?.duration || null,

    // Items
    items: Array.isArray(data?.items) && data.items.length > 0
      ? data.items
      : [{ id: Date.now(), weight: 5, dimensions: { length: 30, width: 20, height: 15 }, sizeCategory: "small" }],
    selectedSize: data?.selectedSize || "small",
    weightKg: typeof data?.weightKg === 'number' ? data.weightKg : 5,

    // Times — unix millis
    pickupTime:         data?.pickupTime         || defaultPickupMs(),
    targetTime:         data?.targetTime         || defaultTargetMs(),
    hasTimeWindow:      Boolean(data?.hasTimeWindow),
    latestPickUpTime:   data?.latestPickUpTime   || defaultLatestPickUpMs(),
    earliestTargetTime: data?.earliestTargetTime || defaultEarliestTargetMs(),

    // Pickup details
    pickupApartment:    data?.pickupApartment    || "",
    pickupFloor:        data?.pickupFloor        || "",
    pickupBuilding:     data?.pickupBuilding     || "",
    pickupAccessCode:   data?.pickupAccessCode   || "",
    pickupInstructions: data?.pickupInstructions || "",

    // Sender
    senderName:  data?.senderName  || "",
    senderPhone: data?.senderPhone || "",
    senderEmail: data?.senderEmail || "",

    // Delivery details
    deliveryApartment:    data?.deliveryApartment    || "",
    deliveryFloor:        data?.deliveryFloor        || "",
    deliveryBuilding:     data?.deliveryBuilding     || "",
    deliveryAccessCode:   data?.deliveryAccessCode   || "",
    deliveryInstructions: data?.deliveryInstructions || "",

    // Recipient
    recipientName:  data?.recipientName  || "",
    recipientPhone: data?.recipientPhone || "",
    recipientEmail: data?.recipientEmail || "",

    // Options
    returnParcels:    Boolean(data?.returnParcels),
    returnMethod:     data?.returnMethod || "bus",
    temperatureControl: Boolean(data?.temperatureControl),
    canBeLeftAtDoor:  Boolean(data?.canBeLeftAtDoor),

    // Billing
    paymentMethod:    data?.paymentMethod    || "invoice",
    yNumber:          data?.yNumber          || "",
    companyName:      data?.companyName      || "",
    paymentReference: data?.paymentReference || "",
    billingDepartment: data?.billingDepartment || "",

    // Transport
    selectedOption:   data?.selectedOption   || null,
    busTransportData: data?.busTransportData || null,

    // Admin
    createdForMemberId: data?.createdForMemberId || undefined,
  };
}

export function useDeliveryDraft(userId) {
  const autoSaveTimeoutRef = useRef(null);

  const loadDraft = useCallback(() => {
    try {
      const stored = localStorage.getItem(DRAFT_KEY);
      if (!stored) return null;

      const draft = JSON.parse(stored);

      if (draft.expiresAt && Date.now() > draft.expiresAt) {
        localStorage.removeItem(DRAFT_KEY);
        return null;
      }

      if (userId && draft.userId && draft.userId !== userId) return null;

      return normalizeDraft(draft.data);
    } catch (error) {
      console.error('Failed to load delivery draft:', error);
      localStorage.removeItem(DRAFT_KEY);
      return null;
    }
  }, [userId]);

  const _write = useCallback((data) => {
    try {
      const stored = localStorage.getItem(DRAFT_KEY);
      let existingData = {};
      if (stored) {
        try { existingData = JSON.parse(stored).data || {}; } catch {}
      }
      const draft = {
        userId: userId || null,
        data: normalizeDraft({ ...existingData, ...data }),
        savedAt: Date.now(),
        expiresAt: Date.now() + DRAFT_EXPIRY_MS,
      };
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    } catch (error) {
      console.error('Failed to save delivery draft:', error);
    }
  }, [userId]);

  /** Debounced save — use for auto-save on state changes */
  const saveDraft = useCallback((data) => {
    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    autoSaveTimeoutRef.current = setTimeout(() => _write(data), 500);
  }, [_write]);

  /** Immediate save — use before navigation */
  const saveDraftImmediate = useCallback((data) => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }
    _write(data);
  }, [_write]);

  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(DRAFT_KEY);
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    } catch (error) {
      console.error('Failed to clear delivery draft:', error);
    }
  }, []);

  useEffect(() => {
    return () => { if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current); };
  }, []);

  return { loadDraft, saveDraft, saveDraftImmediate, clearDraft };
}